<template>
  <div :key="routeKey" :class="{ 'uk-flex': roundStatus === RoundStatus.EDITING_BASIS }">
    <SelectionField
      v-if="!isPublicView && showCounterpointSelect"
      :annotation="teamRepliesAnnotation"
      :counterpoints="counterpoints"
      @close="counterpointClose"
    />
    <div
      class="scroll-view"
      id="counterpoint-scroller"
      ref="counterpoint-scroller"
      :key="isUpdated"
      @scroll="updateScrollerOffset"
      :class="scrollerClasses"
      :style="loading && 'pointer-events:none'"
    >
      <div
        class="counterpoint-viewer uk-width-1-1 uk-flex uk-flex-between uk-position-relative min-height-custom"
        id="relativeRootElement"
        @click="bubbleMenuHide"
      >
        <DebateAlertBanner :show="showAlert" :permission-level="permissionLevel" />

        <div
          v-if="!loading"
          id="mainPanel"
          ref="mainPanel"
          class="main-panel uk-position-relative min-height-custom"
          :class="mainPanelClasses"
          :style="{
            height: '100%',
          }"
        >
          <OutlinePanel
            v-if="showingOutlinePanel"
            id="currentMainPanel"
            ref="annotatorOutline"
            :outlineSource="mainOutlineSource"
            @textClick="handleTextClick"
            @geometriesUpdated="updateOutlineGeometry"
            @markupClick="doMarkupClick"
            @click="doRootTextSingleClick"
            @dblclick="doRootTextDoubleClick"
          />
          <ArticlePanel
            v-if="
              [DocumentType.ARTICLE, DocumentType.TEXT].includes(
                documentSelectedSource.sourceType
              ) &&
              (roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
                roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED)
                ? mainPanelView === 'outline'
                : mainPanelView === 'article' &&
                  counterpointData?.rounds?.length <= maxRoundsForArticlePanel
            "
            :key="isUpdated"
            ref="annotatorArticle"
            :article="documentSelectedSource"
            :enableInput="
              roundStatus === RoundStatus.EDITING_BASIS &&
              permissionLevel !== PermissionLevel.COHORT
            "
            :heading="
              counterpointData?.rounds?.length
                ? 'Article View'
                : documentSelectedSource.sourceType === DocumentType.ARTICLE
                  ? 'Review your Document'
                  : 'Review your Document'
            "
            @textClick="handleTextClick"
            @markupClick="doMarkupClick"
            @geometriesUpdated="updateArticleGeometry"
          />

          <template
            v-if="
              [DocumentType.ARTICLE, DocumentType.TEXT].includes(
                documentSelectedSource.sourceType
              ) &&
              mainPanelView === 'article' &&
              useExcerpts
            "
          >
            <div
              id="rootTextCellsBackground"
              @dblclick="doRootTextDoubleClick"
              @mouseenter="rootTextHoverOut"
            >
              <span v-for="(text, i) in documentSelectedSource.nodes" :key="`bgTxt_${i}`">
                {{ text.nodeContent }}
              </span>
            </div>
            <div id="rootTextCells">
              <RootTextCell
                v-for="rootCell in documentData.compressedText"
                :key="`root${rootCell.annotation._id}`"
                :id="`root-${rootCell.annotation._id}`"
                :ref="`root-${rootCell.annotation._id}`"
                :rootTextCell="rootCell"
                @hoverIn="rootTextHoverIn({ annotation: rootCell.annotation }, ...arguments)"
                @annotationClicked="resetMenu(false)"
                @click="doRootTextSingleClick"
                @dblclick="doRootTextDoubleClick"
              />
            </div>
          </template>
        </div>
        <CounterpointSidePanel
          ref="sidePanel"
          id="counterpointSidePanel"
          @textClick="handleTextClick"
          @markupClick="doMarkupClick"
          v-if="showSidePanel && showingCounterpointSidePanel && !loading"
          :class="['uk-background-default', { 'main-panel-public': isPublicView }]"
          :outlineSource="outline"
          @scroll="doSidePanelScroll"
        />
        <div
          v-if="loading"
          class="uk-position-z-index"
          style="position: absolute; height: 35rem; width: 95%; z-index: 999"
        >
          <ResourceLoadStatus :loading="loading" :loadingMessage="loadingMessage" :solid="true" />
        </div>

        <template v-if="counterpointLoaded && !loading">
          <DebateRound
            v-for="cpData in counterpoints"
            :key="`cp${cpData._id}`"
            :ref="`cp${cpData._id}`"
            class="annotations-panel uk-width-1-4"
            :class="{
              [`annotations-panel-${cpData._id}`]: true,
              'annotations-panel-public': publicView,
            }"
            :cpData="cpData"
            :style="{
              background: getCounterpointColor(cpData) || 'rgba(200, 221, 233, .5)',
            }"
            :scroller-container-id="`scroller_cp_${cpData._id}`"
            :new-annotation-id="newAnnotationId"
            @annotationClicked="handleAnnotationClicked"
            @geometriesUpdated="updateCounterpointGeometry"
          />
        </template>

        <ExcerptOverlay
          v-for="rootCell in documentData.compressedText"
          :key="`rootTextExcerptContainer${rootCell.annotation._id}`"
          :excerpt="rootTextExcerpt[rootCell.annotation._id]"
          :pinned="pinnedRootContext[rootCell.annotation._id] || false"
          :annotation-id="rootCell.annotation._id"
          :show-ellipses="showEllipsesInRootTextNodeExcerpt"
          :update-key="updateRootModel"
          :node-background-color="
            isPublicView
              ? node =>
                  node.underline ? counterpointColors[node.counterpoints[0].team._id] : undefined
              : undefined
          "
          @pin="pinRootContext"
          @close="closeRootContext"
          @mouseleave="rootTextHoverOut"
        />

        <AnnotationVertical v-if="counterpointData?.rounds.length > maxRoundsForArticlePanel" />

        <AnnotationsBubbleMenu
          v-if="
            !isPublicView &&
            !editingAnnotationId &&
            [
              PermissionLevel.TEAM_LEAD,
              PermissionLevel.TEAM_MEMBER,
              PermissionLevel.CURATOR,
              PermissionLevel.COHORT,
              PermissionLevel.HOST,
            ].includes(permissionLevel) &&
            counterpointData?.roundZero?.status !== RoundStatus.EDITING_NLP_OUTLINE
          "
          ref="annotationsMenu"
          :menuPosition="menuPosition"
          :activeAnnotationId="annotationActive"
          @addAnnotation="doAddAnnotation"
          @menuHover="handleBubbleMenuHover"
          @menuLeave="handleBubbleMenuLeave"
          :doResponseRoundAnnotation="doResponseRoundAnnotation"
        />

        <FullContextModal
          v-if="myLatestRoundNumber > maxRoundsForArticlePanel"
          :mode="fullContextModalMode"
          :open="fullContextModalOpen"
          :article="documentSelectedSource"
          :outline="mainOutlineSource"
          ref="fullContextModal"
        />
      </div>

      <CitationModal />

      <EmptyAnnotationModal
        :open="emptyAnnotationModalOpen"
        :empty-annotation="emptyAnnotation"
        @close="emptyAnnotationModalOpen = false"
      />

      <template v-if="isCuratorView">
        <div uk-modal ref="deletedAnnotationModal">
          <BasicModal>
            <div uk-alert class="uk-alert-warning uk-flex" style="align-items: baseline">
              <i uk-icon="warning" style="min-width: 1rem"></i>
              <span v-if="deletedAnnotation" class="uk-margin-small-left uk-text-secondary"
                >The author of an annotation you responded to has withdrawn their annotation</span
              >
            </div>
          </BasicModal>
        </div>
        <div uk-modal ref="updatedAnnotationModal">
          <BasicModal>
            <div uk-alert class="uk-alert-warning uk-flex" style="align-items: baseline">
              <i uk-icon="warning" style="align-items: baseline"></i>
              <span v-if="updatedAnnotation" class="uk-margin-small-left uk-text-secondary"
                >The author has changed their annotation</span
              >
            </div>
          </BasicModal>
        </div>
      </template>
      <OutlineBubbleMenu
        v-if="
          !isPublicView && counterpointData?.roundZero?.status === RoundStatus.EDITING_NLP_OUTLINE
        "
        ref="annotationsMenu"
        :menuPosition="menuPosition"
        :nodeMenu="nodeMenu"
      />
    </div>
    <DropdownBasicPanel
      v-if="!isPublicView && roundStatus === RoundStatus.EDITING_BASIS && framingHide"
      id="dropDownBasicFraming"
      title="Discussion Setup"
      icon="/icons/black/icon-black-book.svg"
      class="framing-box"
      :framingModalHide="true"
    >
      <FieldValueTable
        :fields="baseFields"
        :data="documentData"
        :isCustomField="true"
        :customFieldData="currentTeam?.custom_fields"
        :isDebateView="true"
      />
    </DropdownBasicPanel>
  </div>
</template>

<script>
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
import DocumentType from '@/assets/js/enum/DocumentType';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import { getId, getElementAbsolutePosition, forEachOutlineNode } from '@/assets/js/utils';
import { EventBus } from '@/assets/js/event-bus';
import { dragRootElement } from '@/assets/js/utils';
import DropdownBasicPanel from '@/components/display/general/DropdownBasicPanel';
import FieldValueTable from '@/components/display/general/FieldValueTable';
import CounterpointSidePanel from '@/views/annotations/CounterpointSidePanel';
import ArticlePanel from '@/components/article/ArticlePanel';
import OutlinePanel from '@/components/outline/OutlinePanel';
import RootTextCell from '@/components/article/RootTextCell.vue';
import DebateRound from '@/components/debate/DebateRound';
import AnnotationsBubbleMenu from '@/components/annotations/AnnotationsBubbleMenu';
import CitationModal from '@/components/citations/CitationModal.vue';
import DebateAlertBanner from '@/components/debate/DebateAlertBanner.vue';
import ExcerptOverlay from '@/components/debate/ExcerptOverlay.vue';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import ResourceLoadStatus from '@/components/general/ResourceLoadStatus';
import AnnotationVertical from '@/components/annotations/AnnotationVertical';
import BasicModal from '../../components/display/general/BasicModal.vue';
import FullContextModal from '@/components/debate/FullContextModal.vue';
import EmptyAnnotationModal from '@/components/debate/EmptyAnnotationModal.vue';
import OutlineBubbleMenu from '@/components/annotations/OutlineBubbleMenu';
import SelectionField from '@/components/annotations/SelectionField.vue';
import UIkit from 'uikit';
import { getOutlineNodeForDisplay } from '../../assets/js/utils';
import { xhr } from '@/xhr/endpoints';
import { useOptimizedScroll } from '@/composables/useOptimizedScroll';
import { useDomInteractions } from '@/composables/useDomInteractions';
import {
  getSelectionDirection,
  getNodeKeyAndPositionFromElement,
  getAnnotationNodeInfo,
} from '@/composables/useSelection';

export default {
  name: 'UnifiedDebateView',
  props: {
    viewType: {
      type: String,
      default: 'regular', // Can be 'regular', 'curator', or 'public'
    },
  },
  setup() {
    const { updateScrollerOffset } = useDomInteractions();
    const newScroll = useOptimizedScroll();
    return { updateScrollerOffset, newScroll };
  },
  components: {
    ArticlePanel,
    OutlinePanel,
    RootTextCell,
    DebateRound,
    AnnotationsBubbleMenu,
    CounterpointSidePanel,
    ResourceLoadStatus,
    AnnotationVertical,
    BasicModal,
    FullContextModal,
    EmptyAnnotationModal,
    CitationModal,
    OutlineBubbleMenu,
    SelectionField,
    DropdownBasicPanel,
    FieldValueTable,
    DebateAlertBanner,
    ExcerptOverlay,
  },

  data() {
    return {
      articleAnnotationGeometries: {},
      outlineAnnotationGeometries: {},
      focusAnnotationId: null,
      loading: false,
      rootTextExcerpt: {},
      mainOutlineSource: {},
      counterpoints: [],
      nodeAnnotationsMap: {},
      annotationIds: [],
      isUpdated: 0,
      newAnnotationId: null,
      lastClickedRootCellOrNode: {},
      delta: 0,
      showAlert: false,
      nodeMenu: false,
      saveContentInterval: null,
      loadingMessage: 'Loading...',
      teamRepliesAnnotation: {},
      pinnedRootContext: {},
      routeKey: 0,
      countBubbleMenuOpen: 0,
      framingBtn: false,
      framingHide: true,
      updateOut: false,
      annotationRootHoverId: '',
      updateRootModel: 0,
      selectedAnnRound: 0,
      selectedAnnId: '',
      doResponseRoundAnnotation: false,
      deletedAnnotation: true,
      updatedAnnotation: true,
      fullContextModalOpen: false,
      fullContextModalMode: null,
      emptyAnnotationModalOpen: false,
    };
  },

  computed: {
    isPublicView() {
      return this.viewType === 'public';
    },

    isCuratorView() {
      return this.viewType === 'curator';
    },

    RoundStatus() {
      return RoundStatus;
    },
    PermissionLevel() {
      return PermissionLevel;
    },
    DocumentType() {
      return DocumentType;
    },
    UIkit() {
      return UIkit;
    },

    ...mapState([
      'documentData',
      'debateStatus',
      'menuPosition',
      'showCounterpointSelect',
      'counterpointData',
      'counterpointLoaded',
      'showingCounterpointSidePanel',
      'mainPanelView',
      'scrollSource',
      'publicView',
      'visitor',
      'publicLinkData',
      'UI',
      'counterpointOffsets',
      'articleOffsets',
      'outlineOffsets',
      'counterpointGeometries',
      'nodeId',
      'fullTextView',
      'selectedItem',
      'idToken',
      'generalInstructions',
      'currentTeam',
      'debateNextState',
      'annotationsVisible',
      'counterpointColors',
      'savingAnnotation',
      'emptyAnnotation',
      'basisEdited',
      'counterpointWholeData',
      'annotationActive',
      'editingAnnotationId',
    ]),

    ...mapGetters([
      'userId',
      'teamID',
      'publicLinkID',
      'getRefAnnotationsIds',
      'getRefByAnnotationsIds',
      'getCounterpointColor',
      'myLatestRound',
      'documentSelectedSource',
      'documentOriginalSource',
      'documentNodeAnnotations',
      'myLatestRoundActive',
      'documentPulicLinks',
      'flatOutlineNodes',
      'showingOutlinePanel',
      'useExcerpts',
      'articleExcerpt',
      'myLatestRoundNumber',
      'getVisibleRounds',
      'roundStatus',
      'permissionLevel',
      'getSelectedSource',
      'outlineAnnotations',
      'inlineNodeAnnotations',
      'canEditOutline',
      'getAnnotation',
      'documenFallaciesAnalytic',
    ]),

    showSidePanel() {
      return (
        this.counterpointData?.roundZero?.status === RoundStatus.EDITING_NLP_OUTLINE ||
        this.counterpointData?.roundZero?.status === RoundStatus.ANALYZING_BASIS ||
        this.counterpointData?.roundZero?.status === RoundStatus.EDITING_OUTLINED_ANALYZED ||
        (this.counterpointData?.rounds?.length === 1 &&
          this.documentOriginalSource?.sourceType !== DocumentType.OUTLINE)
      );
    },
    showMiddlePanel() {
      return this.counterpointData?.rounds?.length === 1;
    },
    mainPanelSwitchToMode() {
      return this.mainPanelView === 'outline' ? 'article' : 'outline';
    },

    outline() {
      if (
        this.documentSelectedSource.sourceType === DocumentType.ARTICLE ||
        this.documentSelectedSource.sourceType === DocumentType.TEXT
      ) {
        let AIOutline = this.documentData.sources.find(
          source => source.sourceType === DocumentType.OUTLINE && source.outlineType === 'AI-Edited'
        );
        if (!AIOutline) {
          AIOutline = this.documentData.sources.find(
            source =>
              source.sourceType === DocumentType.OUTLINE && source.outlineType === 'AI-Outline'
          );
        }
        return AIOutline || {};
      }
      if (this.documentSelectedSource.sourceType === DocumentType.OUTLINE) {
        return this.documentSelectedSource;
      }

      return {};
    },

    maxRoundsForArticlePanel() {
      const isCurrentUserCohort = this.permissionLevel === PermissionLevel.COHORT;

      const hasTeamMemberWithCohort = this.currentTeam?.members?.some(
        member =>
          (member.status === 'Accepted' || member.status === 'Invited') &&
          member.teamRole === PermissionLevel.COHORT
      );

      return isCurrentUserCohort || hasTeamMemberWithCohort ? 4 : 3;
    },

    maxRoundsLookbackForAnnotations() {
      const isCurrentUserCohort = this.permissionLevel === PermissionLevel.COHORT;

      const hasTeamMemberWithCohort = this.currentTeam?.members?.some(
        member =>
          (member.status === 'Accepted' || member.status === 'Invited') &&
          member.teamRole === PermissionLevel.COHORT
      );

      const result = isCurrentUserCohort || hasTeamMemberWithCohort ? 4 : 2;

      return result;
    },

    shouldShowRootTextCells() {
      const isArticleOrText = [DocumentType.ARTICLE, DocumentType.TEXT].includes(
        this.documentSelectedSource.sourceType
      );
      const isArticleView = this.mainPanelView === 'article';
      const excerpts = this.useExcerpts;

      return isArticleOrText && isArticleView && excerpts;
    },

    shouldShowArticlePanel() {
      const isArticleOrText = [DocumentType.ARTICLE, DocumentType.TEXT].includes(
        this.documentSelectedSource.sourceType
      );
      const isOutlineEditingMode =
        this.roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
        this.roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED;
      const isOutlineView = this.mainPanelView === 'outline';
      const isArticleView = this.mainPanelView === 'article';
      const roundsCount = this.counterpointData?.rounds?.length || 0;
      const withinMaxRounds = roundsCount <= this.maxRoundsForArticlePanel;

      const shouldShow =
        isArticleOrText &&
        (isOutlineEditingMode ? isOutlineView : isArticleView && withinMaxRounds);

      return shouldShow;
    },

    baseFields() {
      const sourceFields =
        this.documentSelectedSource.sourceType === DocumentType.ARTICLE
          ? [
              {
                label: 'Source Link:',
                field: () => this.documentSelectedSource.sourceURL,
                type: ['function', 'link'],
                allowCopyLink: true,
              },
            ]
          : [];

      const documentFields = [
        {
          label: 'Debate Title:',
          field: 'title',
          sideBySide: false,
          editable:
            this.roundStatus === RoundStatus.EDITING_BASIS &&
            this.permissionLevel !== PermissionLevel.COHORT,
        },
        {
          label: 'Content Title:',
          field: 'articleTitle',
          sideBySide: false,
          editable:
            this.roundStatus === RoundStatus.EDITING_BASIS &&
            this.permissionLevel !== PermissionLevel.COHORT,
        },
        {
          label: 'Publisher:',
          field: 'publication',
          sideBySide: false,
          editable:
            this.roundStatus === RoundStatus.EDITING_BASIS &&
            this.permissionLevel !== PermissionLevel.COHORT,
        },
        {
          label: 'Published:',
          field: 'publicationDate',
          type: ['date'],
          sideBySide: false,
          editable:
            this.roundStatus === RoundStatus.EDITING_BASIS &&
            this.permissionLevel !== PermissionLevel.COHORT,
        },
        {
          label: 'Author:',
          field: 'author',
          sideBySide: false,
          editable:
            this.roundStatus === RoundStatus.EDITING_BASIS &&
            this.permissionLevel !== PermissionLevel.COHORT,
        },
        {
          label: 'Submitted:',
          field: 'createdAt',
          type: ['date'],
          sideBySide: false,
          editable: false,
        },
      ];

      return [...documentFields, ...sourceFields];
    },
    showingPublicLinkOptions() {
      return this.documentPulicLinks(true).length;
    },

    compressedOutline() {
      const filteredOutline = this.filterOutline(
        this.outline?.outline?.outline ? this.outline?.outline?.outline : this.outline?.outline
      );
      const flatOutlineNodes = filteredOutline ? this.flatOutlineNodes(filteredOutline) : [];

      for (const [i, node] of flatOutlineNodes?.entries()) {
        const nodeAnnotations = [
          ...new Set(
            node.nodes?.flatMap(
              textNode => this.documentNodeAnnotations[textNode.nodeKey].counterpointAnnotationIDs
            )
          ),
        ];

        nodeAnnotations.forEach(annId => {
          let j = i + 1;
          while (j < flatOutlineNodes.length) {
            const compNodeAnnotations = flatOutlineNodes[j].nodes.some(textNode =>
              this.documentNodeAnnotations[textNode.nodeKey].counterpointAnnotationIDs.includes(
                annId
              )
            );
            if (compNodeAnnotations) {
              if (flatOutlineNodes[j].parent) {
                const nodeIndex = flatOutlineNodes[j].parent.children?.findIndex(n => {
                  return n._id === flatOutlineNodes[j]._id;
                });
                flatOutlineNodes[j].parent.children?.splice(nodeIndex, 1);
              } else {
                const nodeIndex = filteredOutline.findIndex(n => n._id === flatOutlineNodes[j]._id);
                filteredOutline.splice(nodeIndex, 1);
              }
              node.children = node.children
                ? [flatOutlineNodes[j], ...node.children]
                : [flatOutlineNodes[j]];
              node.sublevel = node.children;

              flatOutlineNodes[j].parent = node;
            }
            j++;
          }
        });
      }
      return {
        ...this.outline,
      };
    },

    showEllipsesInRootTextNodeExcerpt() {
      return [DocumentType.ARTICLE, DocumentType.TEXT].includes(
        this.documentSelectedSource.sourceType
      );
    },

    scrollerClasses() {
      return {
        'counterpoint-topnav-padding-small': !this.UI.showSidebar && this.publicView,
        'counterpoint-topnav-padding-medium': !this.UI.showSidebar && !this.publicView,
        'counterpoint-topnav-padding-large':
          !this.UI.showSidebar && !this.publicView && this.showingPublicLinkOptions,
        'scroll-view': !this.showCounterpointSelect,
        'scroll-view-height-round-zero':
          !this.UI.showSidebar && this.debateNextState?.current_round === 0,
        'scroll-view-height': !this.UI.showSidebar && this.debateNextState?.current_round !== 0,
        'top-margin': this.UI.showSidebar,
        'main-contain-shadow': [
          RoundStatus.EDITING_NLP_OUTLINE,
          RoundStatus.EDITING_BASIS,
          RoundStatus.ANALYZING_BASIS,
          RoundStatus.EDITING_OUTLINED_ANALYZED,
        ].includes(this.roundStatus),
      };
    },

    mainPanelClasses() {
      return {
        'uk-height-1-1 uk-height-viewport':
          this.counterpointData?.rounds.length <= this.maxRoundsForArticlePanel,
        'main-panel-half-view': this.showSidePanel && this.showingCounterpointSidePanel,
        'uk-width-1-2': !this.showingCounterpointSidePanel,
        'main-panel-middle-view': this.showMiddlePanel && this.showingCounterpointSidePanel,
        'main-panel-full-view': !(
          (this.showSidePanel && this.showingCounterpointSidePanel) ||
          this.roundStatus === RoundStatus.EDITING_BASIS ||
          this.roundStatus === RoundStatus.ANALYZING_BASIS ||
          this.roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED
        ),
        'main-show-sidepanel': this.UI.showSidebar,
        'main-panel-fit-view': this.counterpointData?.rounds.length > this.maxRoundsForArticlePanel,
        'main-panel-public': this.publicView,
        'main-panel-shadow-box':
          this.roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
          this.roundStatus === RoundStatus.EDITING_BASIS,
      };
    },
  },

  watch: {
    showingCounterpointSidePanel(val) {
      localStorage.setItem('outlineHideShow', JSON.stringify(val));
    },
    counterpointLoaded(value) {
      if (value && this.counterpointData?.rounds.length <= this.maxRoundsForArticlePanel) {
        document.querySelector('body').style.overflow = 'hidden';
      }
    },
    $route: {
      deep: true,
      immediate: false,
      handler(to, from) {
        // Check if document ID changed OR if we're navigating between different debate states
        const documentChanged = to?.params?.documentID !== from?.params?.documentID;
        const pathChanged = to?.path !== from?.path;

        if (documentChanged) {
          this.$router.routesMap.Debate(to.params.documentID);
          this.setCurrentDocumentID(to.params.documentID);
          this.routeKey += 1;
          if (from !== undefined) {
            this.setRouteCounterpointData(null);
            this.setDebateNextState(null);
            this.setMainPanelView(DocumentType.ARTICLE.toLowerCase());
            this.framingHide = false;
            this.init();
          }
          UIkit.dropdown(document.getElementById('dropModalChildDebateId'))?.hide();
        } else if (
          pathChanged &&
          from?.params?.documentID &&
          to?.params?.documentID === from?.params?.documentID
        ) {
          // Same document but path changed (e.g., from outline mode to debate mode)
          // Reinitialize to pick up state changes
          console.log('Route path changed, reinitializing debate view');
          this.routeKey += 1;
          // Reset state similar to document change
          this.setRouteCounterpointData(null);
          this.setDebateNextState(null);
          this.setMainPanelView(DocumentType.ARTICLE.toLowerCase());
          this.framingHide = false;
          this.init();
        }
      },
    },
    showingOutlinePanel() {
      this.setupRowPositions();
    },

    nodeId() {
      if (!this.canEditOutline) {
        this.isUpdated++;
      }
      this.setupCounterpoints(true);
    },

    counterpointData() {
      this.setupCounterpoints();
      this.reorderAnnotations();
    },

    mainOutlineSource() {
      this.isUpdated++;
    },

    // Watch for round status changes that indicate mode transitions
    // (e.g., from EDITING_NLP_OUTLINE to debate mode after finalizing)
    roundStatus(newStatus, oldStatus) {
      if (!oldStatus || !newStatus) return;

      // Detect transition from outline editing modes to normal debate modes
      const outlineEditModes = [
        RoundStatus.EDITING_NLP_OUTLINE,
        RoundStatus.EDITING_OUTLINED_ANALYZED,
        RoundStatus.ANALYZING_BASIS,
        RoundStatus.EDITING_BASIS,
      ];
      const normalDebateModes = [
        RoundStatus.DRAFTING,
        RoundStatus.FINALIZING,
        RoundStatus.PUBLISHED,
      ];

      const wasEditingMode = outlineEditModes.includes(oldStatus);
      const isNowDebating = normalDebateModes.includes(newStatus);

      if (wasEditingMode && isNowDebating) {
        this.routeKey += 1;
        this.setRouteCounterpointData(null);
        this.setDebateNextState(null);
        this.init();
      }
    },
  },

  beforeCreate() {
    this.$nextTick(() => {
      if (this.counterpointData?.rounds?.length <= 2)
        document.querySelector('body').style.overflow = 'hidden';
    });
  },

  created() {
    this.setCounterpointLoaded(false);
  },

  async mounted() {
    this.init();
    if (!this.isPublicView) {
      if (this.counterpointData?.roundZero?.round === 0) {
        this.setOptionTrue(false);
      } else {
        this.setOptionTrue(true);
      }
    }

    window.addEventListener('resize', this.newScroll);
    EventBus.on('UI.position.annotations', this.newScroll);
    EventBus.on('loader.show', this.showLoader);
    EventBus.on('loader.hide', this.hideLoader);
    EventBus.on('share.twitter', this.openTwitterModal);
    EventBus.on('annotation.delete', this.deleteAnnotationClicked);
    EventBus.on('outline.node.click', this.outlineNodeSingleClick);
    EventBus.on('outline.node.hoverOut', this.rootTextHoverOut);
    EventBus.on('generalinstruction.roundlimit', this.showToastAlert);
    EventBus.on('empty.annotation.show', this.showEmptyAnnotationAlert);
    EventBus.on('root.context.unpin', this.closeRootContext);
    EventBus.on('annotation.teamreplies', this.handleTeamReplies);
    EventBus.on('outline.collapse.expand', this.setupCounterpoints);
    EventBus.on('deabte.init.load', this.init);
    EventBus.on('debate.basis.save.loader.show', this.showLoaderOnLogout);
    EventBus.on('debate.basis.save.loader.hide', this.hideLoaderOnLogout);

    if (!this.isPublicView) {
      EventBus.on('annotation.setup.complete', this.doAnnotationSetupComplete);
      EventBus.on('debate.basis.save', this.saveBasis);
      EventBus.on('debate.basis.save_and_continue', this.saveBasisAndContinue);
      EventBus.on('debate.basis.save_and_invite', this.saveBasisAndInvite);
      EventBus.on('debate.basis.save_request_nlp_outline', this.saveRequestNLPOutline);
      EventBus.on('framing.save.button', this.farmingSaveButton);
      EventBus.on('framing.modal.show.hide', this.farmingShowHide);
      EventBus.on('update.outline.data', this.updateOutlineData);
    }
  },

  updated() {
    if (this.mainPanelView !== 'outline') {
      this.reorderAnnotations();
    }
    this.setupRowPositions();
  },

  beforeUnmount() {
    this.setTeamMode(false);
    EventBus.emit('hide.vertical.view');
    EventBus.emit('hide.modals');
    if (!this.isPublicView && this.counterpointData.rounds.length < 2) {
      EventBus.emit('save.edited.text');
      this.saveBasis();
    }
    this.setMainPanelView(DocumentType.ARTICLE.toLowerCase());
    this.emptyAnnotationModalOpen = false;
    document.querySelector('body').style.overflow = 'auto';
    this.resetMenu(true);
    this.resetDebateView();
    this.framingHide = false;
    EventBus.emit('selection.modal.close');
    window.addEventListener('resize', this.newScroll);
    EventBus.off('UI.position.annotations', this.newScroll);
    EventBus.off('loader.show', this.showLoader);
    EventBus.off('loader.hide', this.hideLoader);
    EventBus.off('share.twitter', this.openTwitterModal);
    EventBus.off('annotation.delete', this.deleteAnnotationClicked);
    EventBus.off('outline.node.click', this.outlineNodeSingleClick);
    EventBus.off('outline.node.hoverOut', this.rootTextHoverOut);
    EventBus.off('generalinstruction.roundlimit', this.showToastAlert);
    EventBus.off('empty.annotation.show', this.showEmptyAnnotationAlert);
    EventBus.off('root.context.unpin', this.closeRootContext);
    EventBus.off('annotation.teamreplies', this.handleTeamReplies);
    EventBus.off('outline.collapse.expand', this.setupCounterpoints);
    EventBus.off('deabte.init.load', this.init);
    EventBus.off('debate.basis.save.loader.show', this.showLoaderOnLogout);
    EventBus.off('debate.basis.save.loader.hide', this.hideLoaderOnLogout);

    if (!this.isPublicView) {
      EventBus.off('annotation.setup.complete', this.doAnnotationSetupComplete);
      EventBus.off('debate.basis.save', this.saveBasis);
      EventBus.off('debate.basis.save_and_continue', this.saveBasisAndContinue);
      EventBus.off('debate.basis.save_and_invite', this.saveBasisAndInvite);
      EventBus.off('debate.basis.save_request_nlp_outline', this.saveRequestNLPOutline);
      EventBus.off('framing.save.button', this.farmingSaveButton);
      EventBus.off('framing.modal.show.hide', this.farmingShowHide);
      EventBus.off('update.outline.data', this.updateOutlineData);
    }
  },

  methods: {
    ...mapMutations([
      'hideTeamReplies',
      'clearAnnotationHoverIds',
      'setNewAnnotationPosition',
      'setMainPanelView',
      'setArticleOffsets',
      'setOutlineOffsets',
      'setFullTextView',
      'incrementScrollCounter',
      'setCounterpointLoaded',
      'setBasisEdited',
      'showCounterpointSidePanel',
      'setAnnotationvisible',
      'setAnnotationTop',
      'setAnnotationActive',
      'setTeamMode',
      'setCurrentDocumentID',
      'setRouteCounterpointData',
      'setDebateNextState',
      'setCurrentTeam',
      'setDocument',
      'setDocuments',
      'setOptionTrue',
    ]),
    ...mapActions([
      'positionMenu',
      'resetMenu',
      'addAnnotation',
      'createNewHostInlineRule',
      'annotationHoverIn',
      'annotationHoverOut',
      'annotationFocusIn',
      'annotationFocusOut',
      'loadOutlineTree',
      'updateCounterpointGeometry',
      'deleteAnnotation',
      'reorderAnnotations',
      'updateRoundStatus',
      'getDocumentWithCounterpoints',
      'getPublicDocumentWithCounterpoints',
      'openTwitterBrowserModal',
      'continueToNextRound',
      'saveAnnotation',
      'simulateContinueToNextRound',
      'addDummyRound',
      'updateDummyRound',
      'runAnalyticDocument',
      'runAnalyticOutlineAsync',
      'getSourceDocumentDetails',
      'setupDocument',
      'getListAnalyticAsync',
    ]),

    async init() {
      try {
        this.showLoader();
        this.setCounterpointLoaded(false);
        if (this.isPublicView) {
          await this.getPublicDocumentWithCounterpoints();
        } else {
          await this.getDocumentWithCounterpoints();
        }
      } catch (error) {
        console.error('[INIT_ERROR] Failed to load debate data:', error);
        this.hideLoader();
        return;
      }
      let outlineHideShow = JSON.parse(localStorage.getItem('outlineHideShow')) || false;
      if (
        this.documentData.sources.length <= 1 ||
        (this.documentData.sources.length === 2 && this.documenFallaciesAnalytic)
      ) {
        outlineHideShow = false;
        localStorage.setItem('outlineHideShow', false);
      }
      this.showCounterpointSidePanel(
        this.counterpointData?.roundZero?.status === RoundStatus.EDITING_NLP_OUTLINE ||
          this.counterpointData?.roundZero?.status === RoundStatus.EDITING_OUTLINED_ANALYZED ||
          outlineHideShow
      );

      this.loadOutlineTree();
      this.setupOutlineSource(
        this.useExcerpts && !this.fullTextView ? this.compressedOutline : this.outline
      );
      if (this.counterpointData?.rounds?.length > this.maxRoundsForArticlePanel) {
        this.rootTextElementAllAnnotations();
      }
      if (
        this.counterpointWholeData?.rounds &&
        this.permissionLevel === PermissionLevel.COHORT &&
        this.isCuratorView
      ) {
        let showAlert = false;
        let showAlertDeleted = false;
        for (const round of this.counterpointWholeData.rounds) {
          for (const annotation of round.annotations) {
            if (annotation.alert) {
              if (this.getAnnotation(annotation.refAnnotation?._id) === undefined) {
                showAlertDeleted = true;
              } else {
                showAlert = true;
              }
              let dataToUpdate = { alert: false };
              setTimeout(async () => {
                try {
                  await xhr.updateAnnotation(annotation._id, dataToUpdate);
                } catch (error) {
                  console.error(error);
                }
              }, 100);
            }
          }
          if (showAlert) break;
        }
        if (showAlert) {
          setTimeout(() => {
            UIkit.modal(this.$refs.updatedAnnotationModal).show();
          }, 200);
        }

        if (showAlertDeleted) {
          setTimeout(() => {
            UIkit.modal(this.$refs.deletedAnnotationModal).show();
          }, 200);
        }
      }
      if (this.documentSelectedSource.sourceType === DocumentType.OUTLINE) {
        this.setMainPanelView(DocumentType.OUTLINE.toLowerCase());
      } else {
        this.setMainPanelView(DocumentType.ARTICLE.toLowerCase());
      }

      if (
        !this.isPublicView &&
        this.counterpointData?.roundZero?.status === RoundStatus.EDITING_BASIS
      ) {
        this.framingHide = true;
      }
      this.reorderAnnotations();
      this.setupRowPositions();

      // Ensure counterpoints are set up after data is loaded
      this.setupCounterpoints();

      setTimeout(() => {
        this.newScroll(0, 'temp');
      }, 1000);

      if (!this.isPublicView) {
        await this.getListAnalyticAsync();
        EventBus.emit('set.list.analytic.async');
      }

      setTimeout(() => {
        this.hideLoader();
      }, 400);
    },
    showLoaderOnLogout() {
      this.loading = true;
    },
    hideLoaderOnLogout() {
      this.loading = false;
    },
    setupOutlineSource(outlineSource) {
      this.mainOutlineSource = outlineSource;
      if (this.mainOutlineSource?.outline?.outline) {
        this.mainOutlineSource.outline.outline = this.mainOutlineSource.outline.outline.map(node =>
          getOutlineNodeForDisplay(node)
        );
      } else if (this.mainOutlineSource?.outline) {
        this.mainOutlineSource.outline = this.mainOutlineSource.outline.map(node =>
          getOutlineNodeForDisplay(node)
        );
      }
      this.resetNodeAnnotationsMap();
      this.setupCounterpoints();
      forEachOutlineNode(
        this.mainOutlineSource?.outline?.outline
          ? this.mainOutlineSource?.outline?.outline
          : this.mainOutlineSource?.outline,
        node => {
          node.open = true;
        }
      );
    },
    showEmptyAnnotationAlert() {
      this.emptyAnnotationModalOpen = true;
    },
    showToastAlert() {
      if (this.permissionLevel === PermissionLevel.COHORT) {
        this.showAlert = true;
        setTimeout(() => {
          UIkit.alert(this.$refs.alertBox).close();
          this.showAlert = false;
        }, 5000);
      }
      if (this.roundStatus !== RoundStatus.DRAFTING) {
        setTimeout(() => {
          this.updateRoundStatus({ status: 'Finalizing' });
        }, 600000);
      }
    },
    bubbleMenuHide(ev) {
      let currentElement = ev.target;
      let containsParagraphs = false;
      while (currentElement) {
        if (currentElement.classList.contains('annotatorArticleData')) {
          containsParagraphs = true;
          break;
        }

        currentElement = currentElement.parentElement;
      }

      if (ev.target.classList.contains('annotatorArticleData')) {
        containsParagraphs = true;
      }

      if (!containsParagraphs) {
        clearTimeout(this.countBubbleMenuOpen);
        this.resetMenu(false);
      }
    },

    handleBubbleMenuHover(annotationId) {
      if (annotationId && this.getAnnotation(annotationId)) {
        const annotation = this.getAnnotation(annotationId);
        this.annotationFocusIn(annotation);
      }
    },

    handleBubbleMenuLeave(annotationId) {
      const annotationElement = document.getElementById(`annotation-${annotationId}`);
      if (annotationElement && !annotationElement.matches(':hover')) {
        this.annotationFocusOut();
      }
    },
    resetDebateView() {
      if (!window.location.href.includes('document-details')) {
        this.setRouteCounterpointData(null);
        this.setDebateNextState(null);
      }
    },
    setupCounterpoints(isNode) {
      if (Object.keys(this.nodeId).length && this.mainPanelView === 'outline' && isNode) {
        let annIds = new Set();
        let parentIds = this.compressedOutline.outline.map(outlineNode => outlineNode._id);
        Object.keys(this.nodeAnnotationsMap).map(id => {
          if (this.nodeId[id] !== false || this.nodeId[id] === 'parent' || parentIds.includes(id)) {
            this.nodeAnnotationsMap[id].map(nodeAnnotationId => {
              annIds.add(nodeAnnotationId);
            });
          }
        });
        annIds = Array.from(annIds);
        const filteredRounds = this.filterCounterpoint(annIds);
        this.setupRowPositions();
        this.counterpoints =
          this.counterpointLoaded && this.counterpointData
            ? this.getVisibleRounds(filteredRounds)
            : [];
      } else {
        this.clearFilterData();
        this.setupRowPositions();
        this.counterpoints =
          this.counterpointLoaded && this.counterpointData
            ? this.getVisibleRounds(this.counterpointData.rounds)
            : [];
      }
    },

    getNodeAnnotationsMap() {
      return this.flatOutlineNodes(
        this.mainOutlineSource?.outline?.outline
          ? this.mainOutlineSource?.outline?.outline
          : this.mainOutlineSource?.outline
      )?.reduce(
        (obj, node) => ({
          ...obj,
          [node._id]: [
            ...new Set(
              node.nodes?.flatMap(n => {
                return this.documentNodeAnnotations[n.nodeKey]?.counterpointAnnotationIDs;
              })
            ),
          ],
        }),
        {}
      );
    },

    resetNodeAnnotationsMap() {
      this.nodeAnnotationsMap = this.getNodeAnnotationsMap();
    },

    doRootTextSingleClick(mouseEvent) {
      clearTimeout(this.countBubbleMenuOpen);
      this.resetMenu(false);
      this.lastClickedRootCellOrNode.targetBox = mouseEvent.target;
      this.lastClickedRootCellOrNode.leftPosition = mouseEvent.target.offsetWidth;
      this.positionTextExcerptPanel();
    },
    rootTextElementAllAnnotations() {
      this.documentData.compressedText?.map(ann => {
        let annotation = ann.annotation;
        this.setRootTextExcerpt(
          annotation,
          annotation,
          value =>
            value.nodeOrder >= annotation.selectedText.from.nodeOrder &&
            value.nodeOrder <= annotation.selectedText.to.nodeOrder
        );
      });
    },
    async doRootTextDoubleClick(nodeId) {
      this.fullContextModalMode = this.mainPanelView === 'outline' ? 'outline' : 'article';
      this.fullContextModalOpen = true;

      await this.$nextTick();

      this.setFullTextView(true);

      let elm = document.getElementsByClassName(nodeId);
      if (elm.length) {
        let defaultStyle = {
          background: elm[0].style.background,
          color: elm[0].style.color,
        };

        if (this.mainPanelView === 'outline') {
          setTimeout(() => {
            elm[elm.length - 1].scrollIntoView({ block: 'center' });
          }, 100);
        } else if (this.mainPanelView === 'article') {
          setTimeout(() => {
            elm[0].scrollIntoView({ block: 'center' });
          }, 100);
        }

        this.$refs.fullContextModal.onHide(async () => {
          this.setFullTextView(false);
          this.fullContextModalOpen = false;
          this.fullContextModalMode = null;
          for (let i = 0; i < elm.length; i++) {
            elm[i].style.background = defaultStyle.background;
            elm[i].style.color = defaultStyle.color;
          }
        });
      } else {
        this.$refs.fullContextModal.onHide(async () => {
          this.setFullTextView(false);
          this.fullContextModalOpen = false;
          this.fullContextModalMode = null;
        });
      }

      (this.documentData?.compressedText || []).forEach(ann => {
        const annotation = ann?.annotation;
        if (!annotation?._id) {
          return;
        }

        if (this.pinnedRootContext[annotation._id]) {
          this.pinnedRootContext[annotation._id] = false;
          this.updateRootModel++;
        }

        const excerptRef = this.$refs[`rootTextExcerptContainer-${annotation._id}`];
        const excerptEl = Array.isArray(excerptRef) ? excerptRef[0] : excerptRef;

        if (excerptEl && excerptEl.style) {
          excerptEl.style.opacity = '0';
          excerptEl.style.display = 'none';
        }
      });
    },
    setupRowPositions() {
      if (!this.useExcerpts) return;
      this.counterpointData?.counterpointsFlat?.forEach(ann => {
        ann.row = -1;
      });

      let row = 1;
      if (this.showingOutlinePanel) {
        let annotationsList = [];
        if (Object.keys(this.nodeId).length) {
          annotationsList = this.annotationIds;
        } else {
          annotationsList = [...new Set(Object.values(this.nodeAnnotationsMap)?.flatMap(v => v))];
        }
        this.setOutlineNodeRowPositions(
          this.mainOutlineSource?.outline?.outline
            ? this.mainOutlineSource?.outline?.outline
            : this.mainOutlineSource.outline,
          row,
          annotationsList
        );
      } else {
        if (this.documentData.compressedText) {
          this.documentData.compressedText.forEach(rText => {
            if (this.counterpointData.counterpointsFlat) {
              let rootTextAnnotation = this.counterpointData.counterpointsFlat.find(
                a => a._id === rText.annotation._id
              );
              rText.row = row;
              row = 1 + this.setupAnnotationRowPositions(rootTextAnnotation, row, []);
            }
          });
        }
      }
    },

    filterCounterpoint(annIds) {
      this.annotationIds = [...annIds];
      let counterData = this.counterpointData.rounds.map(data => ({ ...data }));

      for (let round of counterData) {
        round.annotations = round.annotations.filter(annotation => {
          if (annIds.includes(annotation._id) || annIds.includes(annotation.refAnnotation?._id)) {
            annIds.push(annotation._id);
            return annotation;
          }
        });
      }
      return counterData;
    },

    clearFilterData() {
      this.annotationIds = [];
    },

    setupAnnotationRowPositions(annotation, row, siblings) {
      if (siblings?.length && annotation._id !== siblings[0]._id) row++;

      annotation.row = row;

      let nextAnnotationsOnThread = this.counterpointData.refByAnnotationsMap[annotation._id];
      if (nextAnnotationsOnThread && nextAnnotationsOnThread.length) {
        nextAnnotationsOnThread.sort((ann1, ann2) => {
          let selectedText1 = ann1.selectedText;
          let selectedText2 = ann2.selectedText;
          let nodeDiff = 0;
          nodeDiff =
            (selectedText1?.from?.nodeOrder || 1000) - (selectedText2?.from?.nodeOrder || 1000);
          if (nodeDiff === 0) {
            return (
              (selectedText1?.from?.textPosition || 1000) -
              (selectedText2?.from?.textPosition || 1000)
            );
          }
          return nodeDiff;
        });
        nextAnnotationsOnThread.forEach(threadAnnotation => {
          row = this.setupAnnotationRowPositions(threadAnnotation, row, nextAnnotationsOnThread);
        });
      }

      return row;
    },

    setOutlineNodeRowPositions(outlineNodes, row, annotationsList) {
      outlineNodes?.forEach(node => {
        node.row = row;
        const nodeAnnotations = this.nodeAnnotationsMap[node._id]?.filter(ann =>
          annotationsList.includes(ann)
        );
        const annotationIndices = nodeAnnotations?.map(x =>
          annotationsList.findIndex(y => y === x)
        );
        annotationIndices?.sort((a, b) => b - a);
        if (nodeAnnotations?.length) {
          nodeAnnotations.forEach(annId => {
            row =
              1 +
              this.setupAnnotationRowPositions(
                this.counterpointData.annotationsMap[annId],
                row,
                []
              );
          });
        } else {
          row++;
        }
        if (node.open && node.children?.length) {
          row = this.setOutlineNodeRowPositions(node.children, row, annotationsList);
        }

        node.totalRowHeight = row - node.row;
      });

      return row;
    },

    handleTextClick(data) {
      if (!this.isPublicView) {
        this.openCreateMarkupMenu(data);
      }
    },

    openCreateMarkupMenu(data) {
      if (this.isPublicView) {
        return;
      }

      if (!data || !data.target) {
        return;
      }

      this.doResponseRoundAnnotation =
        data?.target?.ref?.annotation?.author?._id !== this.userId &&
        data.target?.type !== 'article';
      this.selectedAnnRound = data?.target?.ref?.annotation?.roundCounterpoint?.round || 0;
      this.selectedAnnId = data?.target?.ref?.annotation?._id || 'temp';

      if (
        (data.target?.type === 'article' || data.target?.type === 'outlineNode') &&
        this.myLatestRound?.round > this.maxRoundsForArticlePanel
      ) {
        return false;
      }
      if (this.canEditOutline && data.target?.type === 'outlineNode') {
        return false;
      }
      let target;
      let event;
      if (data.from) {
        const newAnnotationPosition = {
          from: {
            nodeId: data.from.id,
            nodeKey: data.from.nodeKey,
            nodeOrder: data.from.nodeOrder,
            textPosition: 0,
          },
          to: {
            nodeId: data.to.id,
            nodeKey: data.to.nodeKey,
            nodeOrder: data.to.nodeOrder,
            textPosition: 0,
          },
        };
        this.setNewAnnotationPosition(newAnnotationPosition);
        return;
      } else {
        target = data.target;
        event = data.event;
      }

      if (
        (!target && !this.canEditOutline) ||
        (this.permissionLevel === PermissionLevel.COHORT && this.myLatestRoundNumber === 2)
      ) {
        return;
      }

      const selection = window.getSelection();

      if (!selection || !selection.anchorNode || !selection.focusNode) {
        return;
      }

      try {
        const direction = getSelectionDirection(selection);
        const anchorElement = selection.anchorNode.id
          ? selection.anchorNode
          : selection.anchorNode.parentElement;
        let focusElement = selection.focusNode.id
          ? selection.focusNode
          : selection.focusNode.parentElement;

        if (!anchorElement || !focusElement) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }

        let customOffset = selection.focusOffset;
        if (anchorElement?.id?.split('_')[2] !== focusElement?.id?.split('_')[2]) {
          if (direction === 1) {
            const children = anchorElement.parentElement?.children;
            if (children?.length > 0) {
              focusElement = [...children].at(-1);
              customOffset = focusElement?.innerHTML?.length || 0;
            }
          } else {
            const children = anchorElement.parentElement?.children;
            if (children?.length > 0) {
              focusElement = [...children][0];
              customOffset = 0;
            }
          }
        }

        let isEmptySelection = false;
        if (selection.anchorNode === selection.focusNode) {
          isEmptySelection =
            selection.anchorOffset - selection.focusOffset === 0 ||
            (Math.abs(selection.anchorOffset - selection.focusOffset) === 1 &&
              !selection.anchorNode.textContent
                ?.charAt(Math.min(selection.anchorOffset, selection.focusOffset))
                ?.trim().length);
        } else {
          isEmptySelection =
            !selection.anchorNode.textContent?.charAt(selection.anchorOffset)?.trim().length &&
            !selection.focusNode.textContent?.charAt(selection.focusOffset)?.trim().length;

          if (isEmptySelection) {
            const idRegex = /_P_(.+)_S_(.+)_N_T(.+)-(.+)/;
            const anchorMatch = idRegex.exec(anchorElement?.id || '');
            const focusMatch = idRegex.exec(focusElement?.id || '');

            if (anchorMatch && focusMatch) {
              const nodesDiff =
                direction > 0
                  ? Math.abs(anchorMatch[4] - focusMatch[3])
                  : Math.abs(anchorMatch[3] - focusMatch[4]);
              isEmptySelection = isEmptySelection && nodesDiff <= 1;
            } else isEmptySelection = false;
          }
        }
        if (isEmptySelection) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }
        const { nodeKey: anchorNodeKeyBase, position: anchorNodePositionBase } =
          getNodeKeyAndPositionFromElement(
            anchorElement,
            selection.anchorOffset,
            'anchor',
            target,
            direction
          );
        if (anchorNodeKeyBase < 0 || anchorNodePositionBase < 0) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }
        const { nodeKey: focusNodeKeyBase, position: focusNodePositionBase } =
          getNodeKeyAndPositionFromElement(focusElement, customOffset, 'focus', target, direction);
        if (focusNodeKeyBase < 0 || focusNodePositionBase < 0) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }
        const nodes =
          target.type === 'annotation'
            ? target.ref?.textNodesMap
            : target.type === 'outlineNode' &&
                target.index !== -1 &&
                target.ref?.outlineSource?.outlineType !== 'Human-Outline'
              ? this.documentData?.outlineObj?.[target.index]?.groundedTextNodes
              : this.documentData?.nodesObj;

        if (!nodes) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }

        const anchorNodeOrder = nodes[anchorNodeKeyBase];
        const focusNodeOrder = nodes[focusNodeKeyBase];

        if (!anchorNodeOrder || !focusNodeOrder) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }

        let nodePositions = {};
        let nodeKeys = {};
        let elements = {};
        if (anchorElement.id < focusElement.id) {
          nodePositions = { anchor: anchorNodePositionBase, focus: focusNodePositionBase };
          nodeKeys = { anchor: anchorNodeKeyBase, focus: focusNodeKeyBase };
          elements = { anchor: anchorElement, focus: focusElement };
        } else if (anchorElement.id > focusElement.id) {
          nodePositions = { anchor: focusNodePositionBase, focus: anchorNodePositionBase };
          nodeKeys = { anchor: focusNodeKeyBase, focus: anchorNodeKeyBase };
          elements = { anchor: focusElement, focus: anchorElement };
        } else if (
          parseInt(anchorNodeOrder.nodeKey?.split('T')[1] || 0) <=
          parseInt(focusNodeOrder.nodeKey?.split('T')[1] || 0)
        ) {
          nodePositions = { anchor: anchorNodePositionBase, focus: focusNodePositionBase };
          nodeKeys = { anchor: anchorNodeKeyBase, focus: focusNodeKeyBase };
          elements = { anchor: anchorElement, focus: focusElement };
        } else {
          nodePositions = { anchor: focusNodePositionBase, focus: anchorNodePositionBase };
          nodeKeys = { anchor: focusNodeKeyBase, focus: focusNodeKeyBase };
          elements = { anchor: focusElement, focus: anchorElement };
        }

        if (!event || !event.srcElement) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }

        this.updateScrollerOffset();
        const activeAnnotationId = event.srcElement.closest('.annotation')?.id?.split('-')[1];
        this.setAnnotationActive(activeAnnotationId);

        if (!this.$refs.annotationsMenu?.$el) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }

        this.positionMenu({ event, menuEl: this.$refs.annotationsMenu.$el });
        clearTimeout(this.countBubbleMenuOpen);
        this.countBubbleMenuOpen = setTimeout(() => {
          this.resetMenu(false);
        }, 5000);
        let outlineGroundedNode = '';
        if (
          target.type === 'outlineNode' &&
          target.index !== -1 &&
          target.ref?.outlineSource?.outlineType !== 'Human-Outline'
        ) {
          outlineGroundedNode = target.index;
        }

        const anchorNodeInfo = nodes[nodeKeys.anchor];
        const focusNodeInfo = nodes[nodeKeys.focus];

        if (!anchorNodeInfo || !focusNodeInfo) {
          clearTimeout(this.countBubbleMenuOpen);
          this.resetMenu(true);
          return;
        }

        const newAnnotationPosition = {
          from: getAnnotationNodeInfo(
            elements.anchor.id,
            nodeKeys.anchor,
            anchorNodeInfo.nodeOrder,
            nodePositions.anchor,
            outlineGroundedNode
          ),
          to: getAnnotationNodeInfo(
            elements.focus.id,
            nodeKeys.focus,
            focusNodeInfo.nodeOrder,
            nodePositions.focus,
            outlineGroundedNode
          ),
        };
        this.setNewAnnotationPosition(newAnnotationPosition);
      } catch (err) {
        clearTimeout(this.countBubbleMenuOpen);
        this.resetMenu(true);
        console.error('[ANNOTATION_DEBUG] Error in openCreateMarkupMenu:', err);
      }
    },

    doMarkupClick(node) {
      if (
        !node ||
        !node.nodeKey ||
        !this.documentNodeAnnotations ||
        !this.documentNodeAnnotations[node.nodeKey]
      ) {
        return;
      }

      const annotationID = this.documentNodeAnnotations[node.nodeKey].counterpointAnnotationIDs[0];
      if (annotationID) {
        this.focusAnnotationId = annotationID;
      }
    },

    handleAnnotationClicked(data) {
      if (!this.isPublicView) {
        this.doAnnotationClicked(data);
      } else {
        this.resetMenu(false);
      }
    },

    doAnnotationClicked({ event, annotation, counterpoint, ref }) {
      if (!event && !annotation && !counterpoint && !ref) {
        this.resetMenu(false);
        return;
      }
      if (this.permissionLevel === PermissionLevel.HOST) {
        this.resetMenu(false);
      } else {
        const selection = window.getSelection();

        if (selection.anchorOffset === selection.focusOffset) {
          const isCurrentUserCohort = this.permissionLevel === this.PermissionLevel.COHORT;

          const hasTeamMemberWithCohort = this.currentTeam?.members?.some(
            member =>
              (member.status === 'Accepted' || member.status === 'Invited') &&
              member.teamRole === this.PermissionLevel.COHORT
          );

          const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;

          const roundDifference = this.myLatestRoundNumber - annotation.roundCounterpoint.round;
          let isValidRoundRange = isCohortTeam
            ? true // Cohort teams: no round-based locking initially
            : roundDifference >= 1 && roundDifference <= this.maxRoundsLookbackForAnnotations;

          // For cohort teams, check if next round exists and is visible
          if (isCohortTeam && isValidRoundRange) {
            const targetRound = annotation.roundCounterpoint?.round;
            const nextRoundNumber = targetRound + 1;
            const nextRound = this.counterpointData?.rounds?.find(r => r.round === nextRoundNumber);

            if (nextRound) {
              // Check if next round is visible
              const visibleRounds = this.getVisibleRounds(this.counterpointData.rounds);
              const isNextRoundVisible = visibleRounds.some(r => r.round === nextRoundNumber);
              isValidRoundRange = isNextRoundVisible;
            } else {
              // Next round doesn't exist - block menu for highest round
              isValidRoundRange = false;
            }
          }

          // Check if target element is valid (not a citation)
          const isValidTarget =
            event.target.classList.value !== 'citation-title' &&
            event.target.classList.value !== 'citation-img';

          // Check if this is a response to someone else's annotation
          const isResponseAnnotation =
            annotation.roundCounterpoint.round !== this.counterpointData.rounds.length &&
            getId(annotation.author) !== this.userId;

          const shouldShowMenu =
            this.myLatestRoundActive &&
            isValidRoundRange &&
            ((this.$refs.annotationsMenu && isValidTarget) || isResponseAnnotation);

          if (shouldShowMenu) {
            this.doResponseRoundAnnotation = isResponseAnnotation;
            this.updateScrollerOffset();
            const annotationEl = event.srcElement?.closest('.annotation');
            if (annotationEl) {
              const activeAnnotationId = annotationEl.id.split('-')[1];
              this.setAnnotationActive(activeAnnotationId);
            }
            this.positionMenu({ event, menuEl: this.$refs.annotationsMenu.$el });
            this.countBubbleMenuOpen = setTimeout(() => {
              this.resetMenu(false);
            }, 5000);
          } else {
            clearTimeout(this.countBubbleMenuOpen);
            this.resetMenu(false);
          }
        } else {
          const isCurrentUserCohort = this.permissionLevel === this.PermissionLevel.COHORT;

          const hasTeamMemberWithCohort = this.currentTeam?.members?.some(
            member =>
              (member.status === 'Accepted' || member.status === 'Invited') &&
              member.teamRole === this.PermissionLevel.COHORT
          );

          const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;

          const roundDifference = this.myLatestRoundNumber - annotation.roundCounterpoint.round;
          let isValidRoundRange = isCohortTeam
            ? true // Cohort teams: no round-based locking initially
            : roundDifference >= 1 && roundDifference <= this.maxRoundsLookbackForAnnotations;

          // For cohort teams, check if next round exists and is visible
          if (isCohortTeam && isValidRoundRange) {
            const targetRound = annotation.roundCounterpoint?.round;
            const nextRoundNumber = targetRound + 1;
            const nextRound = this.counterpointData?.rounds?.find(r => r.round === nextRoundNumber);

            if (nextRound) {
              // Check if next round is visible
              const visibleRounds = this.getVisibleRounds(this.counterpointData.rounds);
              const isNextRoundVisible = visibleRounds.some(r => r.round === nextRoundNumber);
              isValidRoundRange = isNextRoundVisible;
            } else {
              // Next round doesn't exist - block menu for highest round
              isValidRoundRange = false;
            }
          }

          const isCurrentRoundOtherAuthor =
            annotation.roundCounterpoint.round === this.myLatestRoundNumber &&
            getId(annotation.author) !== this.userId;

          if (isValidRoundRange || isCurrentRoundOtherAuthor) {
            this.openCreateMarkupMenu({ event, target: { ref, type: 'annotation' } });
          } else {
            this.resetMenu(false);
          }
        }
      }
    },

    scroll(annotation, targetHeight, staticColumn) {
      if (
        !this.counterpointData ||
        this.counterpointData.rounds.length > this.maxRoundsForArticlePanel
      )
        return;

      this.clearAnnotationHoverIds();
      this.annotationHoverIn(annotation);
      let annotationsToScroll = [
        ...this.getRefAnnotationsIds(annotation),
        ...this.getRefByAnnotationsIds(annotation),
      ];
      annotationsToScroll = [...new Set(annotationsToScroll)];
      annotationsToScroll.forEach(ann => {
        this.scrollToAnnotation(ann, targetHeight, staticColumn);
        if (staticColumn !== 'article' && this.$refs.annotatorArticle) {
          const articleScroller = this.$refs.annotatorArticle.getScroller();
          this.setArticleOffsets({
            top: articleScroller.scrollTop,
            left: this.$refs.annotatorArticle.$el.offsetParent.offsetLeft,
          });
        }
        if (staticColumn !== 'outline' && this.$refs.annotatorOutline) {
          const outlineScroller = this.$refs.annotatorOutline.getScroller();
          this.setOutlineOffsets({
            top: outlineScroller.scrollTop,
            left: this.$refs.annotatorOutline.$el.offsetParent.offsetLeft,
          });
        }
      });
    },

    handleTeamReplies(data) {
      this.teamRepliesAnnotation = data;
      setTimeout(() => {
        let elm = document.getElementById(`annotation-${data._id}`);
        let scrollElm = document.getElementById('counterpoint-scroller');
        let boxElm = document.getElementById('instruction-select').getBoundingClientRect();
        document.getElementsByClassName('counterpoint-viewer')[0].style.marginTop =
          boxElm.height + 'px';
        let container = document
          .getElementById(
            this.mainPanelView === 'article' ? `annotatorArticle` : 'annotatorOutline'
          )
          .getBoundingClientRect();
        let textElm = !data.refAnnotation?._id
          ? document.getElementsByClassName(`counterpoint-ref-${data._id}`)[0]
          : document.getElementById(`annotation-${data.refAnnotation._id}`);
        let scrollPosition = textElm.getBoundingClientRect().top - container.top - 200;
        scrollElm.scroll(0, scrollPosition);
        elm.style.top = scrollPosition + 'px';
        document.getElementById('counterpoint-scroller').style.overflow = 'hidden';
      }, 50);
    },

    counterpointClose() {
      let annElm = document.querySelector(
        `#annotation-${this.teamRepliesAnnotation._id} .annotations-content`
      );
      annElm.style.height = 'auto';
      document.getElementsByClassName('counterpoint-viewer')[0].style.marginTop = '0px';
      document.getElementById('counterpoint-scroller').style.overflow = 'scroll';
      this.incrementScrollCounter();
    },

    scrollToAnnotation(annotationId, targetHeight, staticColumn) {
      if (
        !this.counterpointData ||
        this.counterpointData.rounds.length > this.maxRoundsForArticlePanel
      )
        return;

      let columnData = this.counterpointData.rounds.find(cpData =>
        cpData.annotations?.map(ann => getId(ann)).includes(getId(annotationId))
      );

      if (staticColumn !== `cp${columnData._id}`) {
        const cpRef = this.$refs[`cp${columnData._id}`]
          ? this.$refs[`cp${columnData._id}`][0]
          : null;
        if (cpRef) {
          const cpScroller = cpRef.getScroller();
          this.counterpointOffsets[columnData._id] = {
            top: cpScroller.scrollTop - cpRef.$el.offsetTop - cpRef.$el.offsetParent.offsetTop,
            left: cpRef.$el.offsetLeft,
          };
        }
      }
    },

    async doAddAnnotation(annotationType, reponseRoundAnn) {
      let annotation_id = null;
      try {
        if (annotationType === 'hostInstruction') {
          annotation_id = await this.createNewHostInlineRule(annotationType);
        } else {
          annotation_id = await this.addAnnotation({ annotationType, reponseRoundAnn });
        }
      } catch (err) {
        console.error('[ANNOTATION_DEBUG] Error in doAddAnnotation:', err);
      }

      this.resetNodeAnnotationsMap();
      this.newAnnotationId = annotation_id;
      this.setupCounterpoints(this.mainPanelView === 'outline' ? true : false);

      EventBus.emit('updateoutline');
      this.newScroll(this.selectedAnnRound, this.selectedAnnId);
      this.$forceUpdate();
    },

    async deleteAnnotationClicked() {
      this.loading = true;
      const annotation = this.selectedItem.item;
      await this.deleteAnnotation({
        annotation,
        success: () => (this.loading = false),
        failed: () => (this.loading = false),
      });
      this.newScroll(0, 'temp');
      this.setupCounterpoints(this.mainPanelView === 'outline' ? true : false);
    },

    doAnnotationSetupComplete(annotation_id) {
      if (!annotation_id || !this.newAnnotationId || annotation_id !== this.newAnnotationId) return;
      this.newAnnotationId = null;
      this.loading = false;
      EventBus.emit('vertical.row.setup');
    },
    doSidePanelScroll(ev) {
      if (this.scrollSource === 'sidePanel') {
        const documentEl = window.document.body.closest('html');

        let windowScrollPos =
          documentEl.scrollHeight *
          (ev.srcElement.scrollTop / (ev.srcElement.scrollHeight - ev.srcElement.clientHeight));

        window.scrollTo({ top: windowScrollPos });
      }
    },

    updateArticleGeometry(geometry) {
      this.articleAnnotationGeometries = geometry;
    },

    updateOutlineGeometry(geometry) {
      this.outlineAnnotationGeometries = geometry;
    },

    async updateOutlineData() {
      try {
        const res = await this.saveBasis();
        if (res && res.sourceType === 'Outline') {
          this.setupOutlineSource(res);
        }
      } catch (err) {
        console.error(err);
      }
    },

    filterOutline(nodes, parent) {
      return nodes?.reduce((tree, node) => {
        const newNode = {
          ...node,
          parent,
        };

        if (node.children) {
          newNode.children = this.filterOutline(node.children, newNode);
          newNode.sublevel = newNode.children;
        }
        const hasAnnotations =
          newNode.children?.length ||
          node.textNodes?.some(
            textNode => this.documentNodeAnnotations[textNode.nodeKey].counterpoints.length
          );

        return hasAnnotations ? [...tree, newNode] : tree;
      }, []);
    },

    outlineNodeSingleClick(nodeData, nodeBox) {
      if (this.documentSelectedSource.sourceType === DocumentType.OUTLINE) return;

      const annotations = this.nodeAnnotationsMap[nodeData._id];

      const annotationsInTree = Object.values(this.nodeAnnotationsMap)
        .flatMap(annotation => annotation)
        .filter(annotation => annotations?.includes(annotation));
      if (annotations?.length >= annotationsInTree?.length) return;

      const annotationsObjectsArr = annotations.map(x => this.counterpointData.annotationsMap[x]);
      const firstAnnotation = annotationsObjectsArr[0];
      const lastAnnotation = annotationsObjectsArr[annotationsObjectsArr.length - 1];
      const annotatorOutlineWidth = this.$refs.annotatorOutline
        ? this.$refs.annotatorOutline.$el.offsetWidth
        : 0;
      this.lastClickedRootCellOrNode.targetBox = nodeBox.$el;
      this.lastClickedRootCellOrNode.leftPosition = annotatorOutlineWidth;
      this.setRootTextExcerpt(firstAnnotation, lastAnnotation, value =>
        annotationsObjectsArr.some(
          annotation =>
            value.nodeOrder >= annotation.selectedText.from.nodeOrder &&
            value.nodeOrder <= annotation.selectedText.to.nodeOrder
        )
      );
    },

    rootTextHoverIn({ annotation }) {
      this.annotationRootHoverId = annotation._id;
      this.annotationHoverIn({ annotation });
      dragRootElement(
        document.getElementById(`rootTextExcerptContainer-${annotation._id}`),
        `paragraph-header-${annotation._id}`
      );
    },

    setRootTextExcerpt(firstAnnotation, lastAnnotation, underlineFn) {
      const fromOrder = firstAnnotation?.selectedText?.from?.nodeOrder;
      const toOrder = lastAnnotation?.selectedText?.to?.nodeOrder;

      if (fromOrder === undefined || toOrder === undefined) {
        return;
      }

      const normalizeOrder = order =>
        typeof order === 'number'
          ? order
          : Number.isFinite(parseInt(order, 10))
            ? parseInt(order, 10)
            : order;

      const normalizedFrom = normalizeOrder(fromOrder);
      const normalizedTo = normalizeOrder(toOrder);

      const paddedStart = typeof normalizedFrom === 'number' ? normalizedFrom - 70 : normalizedFrom;

      const paddedEnd = typeof normalizedTo === 'number' ? normalizedTo + 70 : normalizedTo;

      const articleExcerpt = (
        this.articleExcerpt({
          start: paddedStart,
          end: paddedEnd,
        }) || []
      ).filter(node => node && node.paragraphId !== undefined);

      const paragraphs = Object.values(
        articleExcerpt.reduce((acc, value) => {
          if (!value || value.paragraphId === undefined) {
            return acc;
          }

          if (!acc[value.paragraphId]) {
            acc[value.paragraphId] = {
              paragraphId: value.paragraphId,
              nodes: [],
            };
          }

          acc[value.paragraphId].nodes.push({
            ...value,
            underline: typeof underlineFn === 'function' ? !!underlineFn(value) : false,
          });

          return acc;
        }, {})
      );

      this.rootTextExcerpt[firstAnnotation._id] = {
        paragraphs,
      };
    },

    positionTextExcerptPanel() {
      const element = document.getElementById(
        `rootTextExcerptContainer-${this.annotationRootHoverId}`
      );
      if (!element) return;

      const textBubbleBox = this.lastClickedRootCellOrNode.targetBox;
      const leftPosition = this.lastClickedRootCellOrNode.leftPosition;
      element.style.display = 'block';
      element.style.left = `${leftPosition + 5}px`;
      const excerptHeight = element.offsetHeight;
      const textBubblePosition = getElementAbsolutePosition(textBubbleBox);
      const totalExcerptVerticalPixels = textBubblePosition.top + excerptHeight - window.scrollY;
      const alignTop = totalExcerptVerticalPixels < window.innerHeight;
      const textBubbleContainerPosition = getElementAbsolutePosition(this.$refs.mainPanel);
      const paddingGap = 20;
      const topPos = alignTop
        ? textBubblePosition.top - textBubbleContainerPosition.top + paddingGap
        : Math.max(
            textBubblePosition.top - excerptHeight + paddingGap * 2,
            window.scrollY + paddingGap
          );

      element.style.top = `${topPos}px`;
      element.style.opacity = '1';
    },

    rootTextHoverOut() {
      this.documentData?.compressedText?.map(ann => {
        let annotation = ann.annotation;
        if (this.pinnedRootContext[annotation._id]) return;
        const element = document.getElementById(`rootTextExcerptContainer-${annotation._id}`);
        if (element) {
          element.style.opacity = '0';
          element.style.display = 'none';
        }
      });
      this.annotationHoverOut(event);
    },

    pinRootContext(id) {
      this.pinnedRootContext[id] = true;
      this.updateRootModel++;
    },

    closeRootContext(id) {
      if (id === false) {
        if (this.documentData?.compressedText) {
          this.documentData?.compressedText?.map(ann => {
            let annotation = ann.annotation;
            if (this.pinnedRootContext[annotation._id]) {
              this.pinnedRootContext[annotation._id] = false;
              this.updateRootModel++;
            }
            const element = document.getElementById(`rootTextExcerptContainer-${annotation._id}`);
            if (element) {
              element.style.opacity = '0';
              element.style.display = 'none';
            }
          });
        }
      } else {
        this.pinnedRootContext[id] = false;
        const element = document.getElementById(`rootTextExcerptContainer-${id}`);
        if (element) {
          element.style.opacity = '0';
          element.style.display = 'none';
        }
        this.updateRootModel++;
      }
    },

    outlineNodeToggle(outlineNode) {
      if (!outlineNode.open) this.hiddenRows = [outlineNode.row];
      forEachOutlineNode(
        this.mainOutlineSource?.outline?.outline
          ? this.mainOutlineSource?.outline?.outline
          : this.mainOutlineSource.outline,
        node => {
          if (node._id === outlineNode._id) node.open = outlineNode.open;
        }
      );
      this.setupRowPositions();
    },

    openTwitterModal(annotation, url, message) {
      this.openTwitterBrowserModal({
        window,
        linkId: this.publicLinkID,
        docId: this.documentData._id,
        docTitle: this.documentData.title,
        annotation,
        url,
        message,
        visitorId: this.visitor?._id,
      });
    },

    closeTwitterModal() {
      UIkit.modal(this.$refs.twitterModal).hide();
    },

    farmingSaveButton(val) {
      this.framingBtn = !val;
    },

    farmingShowHide(val) {
      this.framingHide = val;
    },

    async saveRequestNLPOutline({ begin, success, failed }) {
      let continueSave = false;
      if (this.framingBtn) {
        if (confirm('You have unsaved changes') == true) continueSave = true;
      } else continueSave = true;
      if (continueSave) {
        begin();
        try {
          if (this.basisEdited) {
            await this.saveBasis();
          }
          this.framingHide = false;
          let res = await this.runAnalyticOutlineAsync({
            generateRunAnalytic: {
              analytic_name: 'Outline-llm',
            },
          });
          let status = await xhr.runAnalyticAsyncStatus(res.source_document_id);
          while (
            status.processing_status === 'processing' ||
            status.processing_status === 'requested'
          ) {
            await new Promise(resolve => setTimeout(resolve, 3000));
            status = await xhr.runAnalyticAsyncStatus(res.source_document_id);
          }
          if (status.processing_status === 'completed') {
            await this.init();
            success();
          } else failed();
        } catch (err) {
          console.error(err);
          failed();
        }
      }
    },

    async saveBasisAndContinue({ begin, success, failed }) {
      if (
        this.debateNextState?.current_status === 'Editing NLP Outline' ||
        this.roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED
      ) {
        this.setMainPanelView(DocumentType.ARTICLE.toLowerCase());
      }
      let continueSave = false;
      if (this.framingBtn) {
        if (confirm('You have unsaved changes') == true) continueSave = true;
      } else continueSave = true;
      if (continueSave) {
        this.loading = true;
        begin();
        try {
          this.showLoader('Saving your basis...');
          this.framingHide = false;
          const res = await this.saveBasis();
          if (
            this.documentData.initialStance === 'Host' &&
            this.debateNextState?.current_round === 0
          ) {
            this.counterpoints = await this.addDummyRound();
          }
          if (res.sourceType === DocumentType.OUTLINE) {
            this.setupOutlineSource(res);
          }
          this.simulateContinueToNextRound();
          if (res.sourceType === DocumentType.TEXT || res.sourceType === DocumentType.ARTICLE) {
            let data = await xhr.getDocumentDetails(this.documentData._id);
            for (let i = 0; i < data.sources.length; i++) {
              const sourceDocumentID = data.sources[i]._id;
              let sourceDocumnetData = await this.getSourceDocumentDetails({ sourceDocumentID });
              data.sources[i].nodes = sourceDocumnetData.nodes;
            }
            this.setDocument(data);
            this.setDocuments(data);
            this.setupDocument(data);
          }
          if (res) {
            const newRound = await this.continueToNextRound({
              team: this.currentTeam,
            });
            if (newRound) {
              this.updateDummyRound(newRound);
              success();
            }

            this.hideLoader();
          } else failed();
        } catch (err) {
          console.error(err);
          failed();
        } finally {
          this.isUpdated++;
          this.loading = false;
        }
      }
    },

    async saveBasisAndInvite({ begin, success, failed }) {
      begin();
      try {
        EventBus.emit('workflow.next.inviteOpposition');
        const res = await this.saveBasis();

        if (res) {
          success();
        } else failed();
      } catch (err) {
        failed();
        console.error(err);
      }
    },

    async saveBasis(generateNLPOutline = undefined) {
      if (
        this.roundStatus === RoundStatus.EDITING_BASIS ||
        this.roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
        this.roundStatus === RoundStatus.ANALYZING_BASIS ||
        this.roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED
      ) {
        if (
          [this.RoundStatus.EDITING_NLP_OUTLINE].includes(this.counterpointData?.roundZero?.status)
        ) {
          return await this.$emitEvent('outline.content.save', null, {
            isAsync: true,
            range: 'first-child',
          });
        } else {
          switch (this.documentData.documentType) {
            case DocumentType.OUTLINE:
              return await this.$emitEvent('outline.content.save', null, {
                isAsync: true,
                range: 'first-child',
              });
            case DocumentType.ARTICLE:
              return await this.$emitEvent(
                'article.content.save',
                { generateNLPOutline },
                { isAsync: true, range: 'first-child' }
              );
            case DocumentType.TEXT:
              return await this.$emitEvent(
                'article.content.save',
                { generateNLPOutline },
                { isAsync: true, range: 'first-child' }
              );
          }
        }
      }
    },

    showLoader(message) {
      this.loading = true;
      this.loadingMessage = message;
    },

    hideLoader() {
      this.loading = false;
      this.loadingMessage = null;
    },
  },
};
</script>
<style lang="scss" scoped>
.main-show-sidepanel {
  margin-top: 2rem;
}

.main-panel {
  margin-left: 5px;
  transition: all 0.3s ease;
  width: calc(45vw - 4rem);

  &-public {
    margin-top: 5rem;
  }

  &-full-view {
    margin-left: 10px;
    margin-right: 10vw;

    @media only screen and (max-width: 1400px) {
      margin-right: 7vw;
    }
  }

  &-fit-view {
    margin-left: 10px !important;
    width: 46vw !important;
    margin-right: 7vw !important;

    @media only screen and (max-width: 1400px) {
      margin-right: 5vw;
    }
  }

  &-half-view {
    margin-left: 47vw;
    width: 47vw;

    @media only screen and (max-width: 1400px) {
      width: 441px;
    }
  }

  &-middle-view {
    margin-left: 34vw;
    width: 33vw;

    @media only screen and (max-width: 1400px) {
      width: 441px;
    }
  }
}

.uk-button-muted {
  background: #e2e2e2 !important;
}

#switchMainPanel {
  margin-top: 0;
  margin-left: -1.5rem;
  z-index: 9;

  select {
    padding-right: 1rem;
  }

  .button-swap {
    background: transparent;

    span:last-child {
      margin-top: -0.35rem;
    }
  }
}

#toggleCounterpointSidePanelBtn {
  left: 5rem;
  position: fixed;
  transform: rotate(-90deg);
  transform-origin: bottom left;
  top: calc(20rem + 2px);
  z-index: 1;
}

.root-text-excerpt-container {
  display: none;
  position: absolute;
  background: #ffffff;
  z-index: 110;
  min-width: 300px;
  max-width: 400px;
  border: 0.5px solid #000000;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.fullview {
  width: 55%;
  height: 90%;
}

.childFullView {
  width: 94.8%;
  height: 85%;
  overflow-y: scroll;
  position: absolute;
  z-index: 9;
  top: 90px !important;
  padding: 1%;
}

#rootTextCells {
  position: absolute;
  top: 0;
  width: 100%;
}

#rootTextCellsBackground {
  color: #aaa;
  height: 100%;
  margin-top: -1.5rem;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    backdrop-filter: blur(0.25rem);
    display: block;
    height: 100%;
    opacity: 1;
    position: absolute;
    width: 100%;
  }
}

.top-margin {
  margin-top: 30px;
}

.min-height-custom {
  min-height: calc(100vh - 180px) !important;
}

.scroll-view {
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(100vh - 120px);
}

.scroll-view-height {
  height: calc(100vh - 120px) !important;
}

.alert-box {
  margin-bottom: 0px !important;
}

.text-select-highlight-round {
  &-same {
    border-radius: 20px;
    width: calc(100% + 16px);
    padding: 0 8px 0 8px;
  }

  &-begin {
    border-radius: 20px 0 0 20px;
    padding-left: 8px;
  }

  &-end {
    border-radius: 0 20px 20px 0;
    padding-right: 11px;
  }
}

.framing-box {
  width: 39vw;
  margin-right: 26px;
  margin-left: 10vw;
  height: fit-content;
  max-height: calc(100vh - 130px);
  overflow: auto;
  position: sticky;
  top: 14%;
}

.paragraph-container {
  padding: 0 1rem !important;
}

.paragraph-header {
  background: #2c3157 !important;
  padding: 13px;
  border-radius: 5px 5px 0 0;
}
</style>

<style lang="scss">
#counterpoint-scroller.main-contain-shadow {
  height: calc(100vh - 165px) !important;
  box-shadow:
    0 0px 0px rgba(0, 0, 0, 0.25),
    0 10px 10px rgba(0, 0, 0, 0.25);
  width: fit-content !important;

  .side-panel {
    left: 54vw;
  }

  .main-panel {
    margin-left: 0px !important;
  }
}
</style>
